import { get, post, del } from './client'
import type {
  Manufacturer,
  Model,
  Motor,
  VehicleLookupResult,
  SvvVehicle,
  CarDetails,
  RmiVehicleImage,
  RmiGroupImage,
  Article,
  ArticleDetail,
  ArticleFitment,
  Category,
  ArticleContent,
  MandatoryInfoItem,
  ImageInfo,
} from '../types'

// Build a `?vin=...&motorCode=...` suffix for parts-fetching endpoints.
// Both fields are optional; an empty suffix is returned when nothing is known.
function buildFitmentQuery(vin?: string | null, motorCode?: string | null): string {
  const params: string[] = []
  if (vin && vin.trim().length > 0) params.push(`vin=${encodeURIComponent(vin.trim())}`)
  if (motorCode && motorCode.trim().length > 0) params.push(`motorCode=${encodeURIComponent(motorCode.trim())}`)
  return params.length > 0 ? `?${params.join('&')}` : ''
}

// Vehicle search
export const searchByRegNr = (regNr: string) =>
  get<VehicleLookupResult>(`/vehicle/by-reg/${encodeURIComponent(regNr)}`)

export const getSvvDetails = (regNr: string) =>
  get<SvvVehicle>(`/svv/${encodeURIComponent(regNr)}`)

export const getVehicleById = (carId: number) =>
  get<CarDetails>(`/vehicle/${carId}`)

export const getVehicleLookup = (carId: number) =>
  get<VehicleLookupResult>(`/vehicle/lookup/${carId}`)

export const getVehicleByRmiId = (carId: number) =>
  get<VehicleLookupResult>(`/vehicle/rmi/${carId}`)

export const getRmiFilterChoice = (carId: number) =>
  get<VehicleLookupResult>(`/vehicle/rmi-filter/${carId}`)

// Manual car selection
export const getManufacturers = () =>
  get<Manufacturer[]>('/manufacturers')

export const getModels = (manuId: number) =>
  get<Model[]>(`/manufacturers/${manuId}/models`)

export const getMotors = (manuId: number, modelId: number) =>
  get<Motor[]>(`/manufacturers/${manuId}/models/${modelId}/motors`)

// RMI graphical navigation
export const getRmiVehicleImage = (qualId: string, carId: number) =>
  get<RmiVehicleImage>(`/rmi/vehicle-image/${qualId}/${carId}`)

export const getRmiMainGroupImage = (qualId: string, carId: number, mainGroupId: string) =>
  get<RmiGroupImage>(`/rmi/maingroup-image/${qualId}/${carId}/${mainGroupId}`)

export const getRmiSubGroupImage = (qualId: string, carId: number, mainGroupId: string, subGroupId: string) =>
  get<RmiGroupImage>(`/rmi/subgroup-image/${qualId}/${carId}/${mainGroupId}/${subGroupId}`)

export const getRmiProducts = (
  qualId: string,
  carId: number,
  mainGroupId: string,
  subGroupId: string,
  itemId: number,
  vin?: string | null,
  motorCode?: string | null,
) =>
  get<Article[]>(`/rmi/products/${qualId}/${carId}/${mainGroupId}/${subGroupId}/${itemId}${buildFitmentQuery(vin, motorCode)}`)

// Standard category navigation
export const getCategories = (carId: number, parentId: number) =>
  get<Category[]>(`/vehicles/${carId}/categories?parentId=${parentId}`)

export const getCategoryParts = (
  carId: number,
  catId: number,
  vin?: string | null,
  motorCode?: string | null,
) =>
  get<Article[]>(`/vehicles/${carId}/categories/${catId}/parts${buildFitmentQuery(vin, motorCode)}`)

// OEM search
export const searchByOem = (
  oeNum: string,
  vin?: string | null,
  motorCode?: string | null,
) =>
  get<Article[]>(`/oem/${encodeURIComponent(oeNum)}${buildFitmentQuery(vin, motorCode)}`)

// Config
export interface HiddenItem {
  id: string
  mode: 'whitebox' | 'opacity'
}

export interface CustomRmiLink {
  categoryId: number
  label: string
  sortOrder: number
  // RMI scope the link was pinned at. null/null = vehicle level, mg only = main-group level,
  // mg + sg = sub-group level. Matching is exact at display time.
  mainGroupId?: string | null
  subGroupId?: string | null
  // Editor-chosen feather-style icon, keyed into SHORTCUT_ICONS. Null = fallback default.
  iconCode?: string | null
}

export interface TecdocConfigResponse {
  hiddenItems: HiddenItem[]
  hideProductsNotInStore: boolean
  categoryNames: Record<string, string>
  pinnedManufacturers: string[]
  canEditCategories: boolean
  customRmiLinks: CustomRmiLink[]
}

export const getTecdocConfig = () =>
  get<TecdocConfigResponse>('/config/hidden-categories')

export const saveCategoryName = (id: string, name: string | null) =>
  post<{ categoryNames: Record<string, string> }>('/config/category-name', { id, name })

export const saveCustomRmiLink = (payload: CustomRmiLink) =>
  post<CustomRmiLink[]>('/config/custom-rmi-link', payload)

export const deleteCustomRmiLink = (
  categoryId: number,
  mainGroupId?: string | null,
  subGroupId?: string | null,
) => {
  const params: string[] = []
  if (mainGroupId) params.push(`mg=${encodeURIComponent(mainGroupId)}`)
  if (subGroupId) params.push(`sg=${encodeURIComponent(subGroupId)}`)
  const qs = params.length ? `?${params.join('&')}` : ''
  return del<CustomRmiLink[]>(`/config/custom-rmi-link/${categoryId}${qs}`)
}

// Hidden category management (TecdocEditor only)
export const saveHiddenCategory = (id: string, label: string, hideMode: 'whitebox' | 'opacity') =>
  post<{ hiddenItems: HiddenItem[] }>('/config/hidden-category', { id, label, hideMode })

export const deleteHiddenCategory = (id: string) =>
  del<{ hiddenItems: HiddenItem[] }>(`/config/hidden-category?id=${encodeURIComponent(id)}`)

export interface ArticleDebugResponse {
  sku: string
  current: unknown
  currentError: string | null
  withExtraFlags: unknown
  withExtraFlagsError: string | null
}

export const getArticleDebug = (sku: string) =>
  get<ArticleDebugResponse>(`/debug/article/${encodeURIComponent(sku)}`)

// Product page enrichment
export const getArticleMandatory = (sku: string) =>
  get<MandatoryInfoItem[]>(`/articles/${encodeURIComponent(sku)}/mandatory`)

export const getArticleTitle = (sku: string) =>
  get<{ manufacturer: string; description: string }>(`/articles/${encodeURIComponent(sku)}/title`)

export const getArticleImages = (sku: string) =>
  get<ImageInfo[]>(`/articles/${encodeURIComponent(sku)}/images`)

export const getArticleDetails = (sku: string) =>
  get<ArticleDetail>(`/articles/${encodeURIComponent(sku)}/details`)

// Cold product-page fitment table (lazy-loaded): vehicles/engines + VIN/year limits.
export const getArticleFitment = (sku: string) =>
  get<ArticleFitment>(`/articles/${encodeURIComponent(sku)}/fitment`)

export const batchCheckContent = (skus: string[]) =>
  post<ArticleContent[]>('/articles/batch-check', { skus })
